/**
 * Utility functions for the application
 */

/**
 * Sleep for a specified duration
 * @param ms Duration to sleep in milliseconds
 * @returns Promise that resolves after the specified duration
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Format a date to a human-readable string
 * @param date Date to format
 * @returns Formatted date string
 */
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a time to a human-readable string
 * @param date Date to format
 * @returns Formatted time string
 */
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useEffect } from 'react';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Automatically selects all text in an input field when focused
 * Useful for number inputs to make them easier to edit on mobile
 * @param event The focus event from the input element
 */
export function selectInputContentOnFocus(event: React.FocusEvent<HTMLInputElement> | FocusEvent): void {
  if (event.target instanceof HTMLInputElement) {
    event.target.select();
  }
}

/**
 * React hook that adds global event listeners to automatically select content
 * in number inputs on focus.
 * 
 * Usage: Just call this hook once in your main App component
 */
export function useSelectInputOnFocus(): void {
  useEffect(() => {
    // Handler to select input content when focused
    const handleFocus = (event: FocusEvent) => {
      // Only apply to number inputs
      if (event.target instanceof HTMLInputElement && event.target.type === 'number') {
        event.target.select();
      }
    };

    // Add event listener
    document.addEventListener('focusin', handleFocus);

    // Cleanup
    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, []);
}